import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, Link2, UserPlus, ShieldAlert, Search, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function UsersMigrationPage() {
  const { axiosAuth, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const isSuperAdmin = currentUser?.role === "super_admin";

  const [orphans, setOrphans] = useState([]);
  const [loading, setLoading] = useState(true);

  // Link existing dialog
  const [linkUser, setLinkUser] = useState(null);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [empQuery, setEmpQuery] = useState("");
  const [selectedEmpId, setSelectedEmpId] = useState("");

  // Create quick employee dialog
  const [quickUser, setQuickUser] = useState(null);
  const [quickForm, setQuickForm] = useState({
    person_type: "empleado", first_name: "", last_name: "", company_name: "",
    email: "", phone: "", center_id: "", department_id: "", position_id: "",
    direct_manager_id: "", internal_notes: "",
  });
  const [centers, setCenters] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);

  // Exception dialog
  const [exceptionUser, setExceptionUser] = useState(null);
  const [exceptionForm, setExceptionForm] = useState({ reason: "tecnico_sistema", notes: "" });

  const loadOrphans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosAuth().get("/users/orphans");
      setOrphans(res.data.items || []);
    } catch { toast.error("Error al cargar usuarios huérfanos"); }
    finally { setLoading(false); }
  }, [axiosAuth]);

  const loadCatalogs = useCallback(async () => {
    try {
      const [c, d, p] = await Promise.all([
        axiosAuth().get("/centers"),
        axiosAuth().get("/departments"),
        axiosAuth().get("/positions"),
      ]);
      setCenters(c.data || []); setDepartments(d.data || []); setPositions(p.data || []);
    } catch { /* ignore */ }
  }, [axiosAuth]);

  useEffect(() => { loadOrphans(); loadCatalogs(); }, [loadOrphans, loadCatalogs]);

  useEffect(() => {
    if (!linkUser) return;
    const t = setTimeout(async () => {
      try {
        const res = await axiosAuth().get(`/employees/available-for-link?q=${encodeURIComponent(empQuery)}`);
        setAvailableEmployees(res.data || []);
      } catch { /* ignore */ }
    }, 200);
    return () => clearTimeout(t);
  }, [empQuery, linkUser, axiosAuth]);

  const doLink = async () => {
    if (!linkUser || !selectedEmpId) { toast.error("Selecciona un empleado"); return; }
    try {
      await axiosAuth().put(`/users/${linkUser.id}/link-employee`, { employee_id: selectedEmpId });
      toast.success("Usuario vinculado");
      setLinkUser(null); setSelectedEmpId(""); setEmpQuery("");
      loadOrphans();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al vincular"); }
  };

  const doQuickCreate = async () => {
    if (!quickUser) return;
    if (!quickForm.first_name && !quickForm.company_name) {
      toast.error("Indica nombre o razón social"); return;
    }
    try {
      await axiosAuth().post(`/users/${quickUser.id}/create-employee`, quickForm);
      toast.success("Ficha creada y vinculada");
      setQuickUser(null);
      setQuickForm({ person_type: "empleado", first_name: "", last_name: "", company_name: "",
        email: "", phone: "", center_id: "", department_id: "", position_id: "",
        direct_manager_id: "", internal_notes: "" });
      loadOrphans();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al crear ficha"); }
  };

  const doException = async () => {
    if (!exceptionUser) return;
    if (!isSuperAdmin) { toast.error("Solo Super Admin puede marcar excepciones"); return; }
    if (!exceptionForm.notes || exceptionForm.notes.length < 5) {
      toast.error("Justificación mínima 5 caracteres"); return;
    }
    try {
      await axiosAuth().put(`/users/${exceptionUser.id}/exception`, {
        enabled: true, reason: exceptionForm.reason, notes: exceptionForm.notes,
      });
      toast.success("Excepción marcada");
      setExceptionUser(null); setExceptionForm({ reason: "tecnico_sistema", notes: "" });
      loadOrphans();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-4" data-testid="users-migration-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <button onClick={() => navigate("/admin/usuarios")} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 mb-1" data-testid="back-link">
            <ArrowLeft className="w-3 h-3" /> Volver a Usuarios
          </button>
          <h1 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 uppercase">Migración de usuarios</h1>
          <p className="text-sm text-slate-500 mt-1">
            Vincula a una persona, crea una ficha rápida o marca como excepción cada usuario sin vinculación.
          </p>
        </div>
        <Badge className="bg-amber-100 text-amber-800 border border-amber-200 rounded-sm text-xs px-2 py-1" data-testid="orphans-counter">
          <AlertTriangle className="w-3 h-3 inline mr-1" /> {orphans.length} pendiente{orphans.length === 1 ? "" : "s"}
        </Badge>
      </div>

      {/* Info banner */}
      <Card className="border-slate-200 bg-slate-50 rounded-sm">
        <CardContent className="p-3 text-xs text-slate-600 space-y-1">
          <p><strong>Regla del sistema:</strong> todo usuario debe estar vinculado a una persona (empleado, externo, proveedor o técnico). Para consultores externos crea una ficha tipo <em>externo</em> o <em>proveedor</em>.</p>
          <p>Las únicas excepciones admitidas son: <strong>Super Administrador inicial</strong> y <strong>Cuenta técnica de sistema</strong>.</p>
        </CardContent>
      </Card>

      {/* Orphans list */}
      <Card className="border-slate-200 rounded-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Usuario</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Email</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Rol</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Alta</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-right">Acción</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Cargando...</TableCell></TableRow>
              ) : orphans.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-500" data-testid="no-orphans">
                  <CheckCircle2 className="w-6 h-6 mx-auto text-green-600 mb-2" />
                  Perfecto — no hay usuarios sin vinculación.
                </TableCell></TableRow>
              ) : orphans.map(u => (
                <TableRow key={u.id} data-testid={`orphan-row-${u.id}`}>
                  <TableCell className="text-sm font-medium text-slate-800">{u.name || "(sin nombre)"}</TableCell>
                  <TableCell className="text-xs text-slate-600">{u.email}</TableCell>
                  <TableCell className="text-xs text-slate-600">{u.role}</TableCell>
                  <TableCell className="text-xs text-slate-400 font-mono">{u.created_at?.slice(0, 10) || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center gap-1 justify-end flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => { setLinkUser(u); setSelectedEmpId(""); setEmpQuery(""); }}
                        className="text-xs h-7 rounded-sm" data-testid={`link-existing-${u.id}`}>
                        <Link2 className="w-3 h-3 mr-1" /> Vincular
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setQuickUser(u); setQuickForm({...quickForm, email: u.email, first_name: u.name?.split(" ")[0] || "", last_name: u.name?.split(" ").slice(1).join(" ") || ""}); }}
                        className="text-xs h-7 rounded-sm" data-testid={`quick-create-${u.id}`}>
                        <UserPlus className="w-3 h-3 mr-1" /> Crear ficha
                      </Button>
                      {isSuperAdmin && (
                        <Button size="sm" variant="outline" onClick={() => setExceptionUser(u)}
                          className="text-xs h-7 rounded-sm border-amber-300 text-amber-700 hover:bg-amber-50" data-testid={`exception-${u.id}`}>
                          <ShieldAlert className="w-3 h-3 mr-1" /> Excepción
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Link Existing Dialog */}
      <Dialog open={!!linkUser} onOpenChange={() => setLinkUser(null)}>
        <DialogContent className="max-w-md rounded-sm" data-testid="link-existing-dialog">
          <DialogHeader>
            <DialogTitle className="heading-industrial text-lg uppercase">Vincular a empleado/persona</DialogTitle>
            <p className="text-xs text-slate-500">Usuario: {linkUser?.name} ({linkUser?.email})</p>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input value={empQuery} onChange={(e) => setEmpQuery(e.target.value)}
                placeholder="Nombre, email, razón social..." className="pl-8 h-9 text-xs rounded-sm border-slate-200" data-testid="link-search-input" />
            </div>
            <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-sm">
              {availableEmployees.length === 0 ? (
                <div className="p-3 text-center text-xs text-slate-400">Sin resultados.</div>
              ) : availableEmployees.map(emp => (
                <button key={emp.id} type="button" onClick={() => setSelectedEmpId(emp.id)}
                  className={`w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-slate-50 ${selectedEmpId === emp.id ? "bg-slate-100" : ""}`}
                  data-testid={`link-emp-${emp.id}`}>
                  <p className="text-xs font-medium text-slate-800">{emp.name || emp.company_name}</p>
                  <p className="text-[10px] text-slate-500">{emp.email || "—"} · {emp.center_name || emp.center || "—"}</p>
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkUser(null)} className="rounded-sm text-xs uppercase">Cancelar</Button>
            <Button onClick={doLink} disabled={!selectedEmpId} className="bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 rounded-sm text-xs uppercase font-semibold" data-testid="link-submit">Vincular</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick create Dialog */}
      <Dialog open={!!quickUser} onOpenChange={() => setQuickUser(null)}>
        <DialogContent className="max-w-xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="quick-create-dialog">
          <DialogHeader>
            <DialogTitle className="heading-industrial text-lg uppercase">Crear ficha rápida</DialogTitle>
            <p className="text-xs text-slate-500">Usuario: {quickUser?.name} ({quickUser?.email})</p>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Tipo de persona</Label>
              <Select value={quickForm.person_type} onValueChange={(v) => setQuickForm({...quickForm, person_type: v})}>
                <SelectTrigger className="rounded-sm h-9 border-slate-200" data-testid="qf-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="empleado">Empleado</SelectItem>
                  <SelectItem value="externo">Externo / Consultor</SelectItem>
                  <SelectItem value="proveedor">Proveedor</SelectItem>
                  <SelectItem value="tecnico_sistema">Técnico de sistema</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Nombre {quickForm.person_type === "proveedor" ? "" : "*"}</Label>
                <Input value={quickForm.first_name} onChange={(e) => setQuickForm({...quickForm, first_name: e.target.value})} className="rounded-sm h-9 border-slate-200" data-testid="qf-first" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Apellidos</Label>
                <Input value={quickForm.last_name} onChange={(e) => setQuickForm({...quickForm, last_name: e.target.value})} className="rounded-sm h-9 border-slate-200" data-testid="qf-last" />
              </div>
              {(quickForm.person_type === "externo" || quickForm.person_type === "proveedor") && (
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Razón social / Empresa</Label>
                  <Input value={quickForm.company_name} onChange={(e) => setQuickForm({...quickForm, company_name: e.target.value})} className="rounded-sm h-9 border-slate-200" data-testid="qf-company" />
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Email ficha</Label>
                <Input type="email" value={quickForm.email} onChange={(e) => setQuickForm({...quickForm, email: e.target.value})} className="rounded-sm h-9 border-slate-200" data-testid="qf-email" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Teléfono</Label>
                <Input value={quickForm.phone} onChange={(e) => setQuickForm({...quickForm, phone: e.target.value})} className="rounded-sm h-9 border-slate-200" data-testid="qf-phone" />
              </div>
              {quickForm.person_type === "empleado" && (
                <>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Centro</Label>
                    <Select value={quickForm.center_id || "_"} onValueChange={(v) => setQuickForm({...quickForm, center_id: v === "_" ? "" : v})}>
                      <SelectTrigger className="rounded-sm h-9 border-slate-200"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_">—</SelectItem>
                        {centers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Departamento</Label>
                    <Select value={quickForm.department_id || "_"} onValueChange={(v) => setQuickForm({...quickForm, department_id: v === "_" ? "" : v})}>
                      <SelectTrigger className="rounded-sm h-9 border-slate-200"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_">—</SelectItem>
                        {departments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1 sm:col-span-2">
                    <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Puesto</Label>
                    <Select value={quickForm.position_id || "_"} onValueChange={(v) => setQuickForm({...quickForm, position_id: v === "_" ? "" : v})}>
                      <SelectTrigger className="rounded-sm h-9 border-slate-200"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_">—</SelectItem>
                        {positions.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div className="space-y-1 sm:col-span-2">
                <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Observaciones</Label>
                <Textarea value={quickForm.internal_notes} onChange={(e) => setQuickForm({...quickForm, internal_notes: e.target.value})} rows={2} className="rounded-sm border-slate-200 text-xs" data-testid="qf-notes" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickUser(null)} className="rounded-sm text-xs uppercase">Cancelar</Button>
            <Button onClick={doQuickCreate} className="bg-slate-900 hover:bg-slate-800 rounded-sm text-xs uppercase font-semibold" data-testid="qf-submit">Crear y vincular</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exception dialog */}
      <Dialog open={!!exceptionUser} onOpenChange={() => setExceptionUser(null)}>
        <DialogContent className="max-w-md rounded-sm" data-testid="exception-dialog">
          <DialogHeader>
            <DialogTitle className="heading-industrial text-lg uppercase">Marcar como excepción</DialogTitle>
            <p className="text-xs text-slate-500">Usuario: {exceptionUser?.name} ({exceptionUser?.email})</p>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-800">
              <ShieldAlert className="w-3.5 h-3.5 inline mr-1" />
              Solo permitido para Super Admin inicial o cuentas técnicas. Quedará auditado.
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-700">Motivo</Label>
              <Select value={exceptionForm.reason} onValueChange={(v) => setExceptionForm({...exceptionForm, reason: v})}>
                <SelectTrigger className="rounded-sm h-9 border-slate-200" data-testid="exc-reason"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tecnico_sistema">Cuenta técnica de sistema</SelectItem>
                  <SelectItem value="super_admin_initial">Super Administrador inicial</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] uppercase tracking-wider font-semibold text-slate-700">Justificación (mín. 5 caracteres)</Label>
              <Textarea value={exceptionForm.notes} onChange={(e) => setExceptionForm({...exceptionForm, notes: e.target.value})} rows={2} className="rounded-sm border-slate-200 text-xs" data-testid="exc-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExceptionUser(null)} className="rounded-sm text-xs uppercase">Cancelar</Button>
            <Button onClick={doException} className="bg-amber-700 hover:bg-amber-800 rounded-sm text-xs uppercase font-semibold" data-testid="exc-submit">Confirmar excepción</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
